// ── Bank-feed sync (browser side) ──
//
// After an account is linked, this pulls the latest transactions from Stitch and
// upserts them as RAW rows into {parent}/{id}/bankFeedTransactions, tagged
// status:'awaiting_classification' and visible to every member (the anti-fraud
// transparency decision — no one can quietly sit on a transaction). Categorisation and
// posting stay in the existing review flow. Dedups by __bankTxnId so re-syncs are safe.
//
// `parent` is "projects" (business bookkeeping) or "stokvels" (savings groups) — the
// two surfaces share this plumbing but live under different top-level collections.
//
// Dormant-safe: syncBankFeed refuses (BankFeedDisabledError) while the feature is off.

import { db } from "../firebase";
import { doc, setDoc, getDoc, collection, getDocs, writeBatch, serverTimestamp } from "firebase/firestore";
import { isBankFeedEnabled, BankFeedDisabledError, PATHS } from "./config";
import { fetchTransactions } from "./stitchClient";
import { normalizeStitchTransactions } from "./normalizeStitch";

const configDoc = (parent, id) => doc(db, parent, id, PATHS.config[0], PATHS.config[1]);
const txnCol = (parent, id) => collection(db, parent, id, PATHS.transactions);

// Firestore doc ids can't contain '/'; Stitch ids are opaque, so sanitise defensively.
function safeId(id) {
  return String(id || "").replace(/[/.#$[\]]/g, "_");
}

export async function loadBankFeedConfig(id, parent = "projects") {
  if (!id) return null;
  const snap = await getDoc(configDoc(parent, id));
  return snap.exists() ? snap.data() : null;
}

export async function saveBankFeedLink(id, link, parent = "projects") {
  if (!id) return;
  await setDoc(configDoc(parent, id), { ...link, linkedAt: serverTimestamp() }, { merge: true });
}

// Pull latest transactions and upsert new ones. Returns the count added.
export async function syncBankFeed(id, parent = "projects") {
  if (!isBankFeedEnabled()) throw new BankFeedDisabledError();
  if (!id) throw new Error("No account.");
  const cfg = await loadBankFeedConfig(id, parent);
  if (!cfg || !cfg.linkId) throw new Error("No bank account linked yet.");

  const raw = await fetchTransactions(cfg.linkId, { first: 100 });
  const rows = normalizeStitchTransactions(raw, "Stitch bank feed");

  const existing = await getDocs(txnCol(parent, id));
  const seen = new Set(existing.docs.map((d) => d.id));

  const batch = writeBatch(db);
  let added = 0;
  rows.forEach((r) => {
    const rid = safeId(r.__bankTxnId);
    if (!rid || seen.has(rid)) return;
    batch.set(doc(txnCol(parent, id), rid), {
      ...r,
      status: "awaiting_classification",
      syncedAt: serverTimestamp(),
    });
    added++;
  });
  if (added) await batch.commit();
  await setDoc(configDoc(parent, id), { lastSync: serverTimestamp() }, { merge: true });
  return added;
}

// Convenience wrappers for the stokvel surface.
export const loadStokvelBankFeedConfig = (id) => loadBankFeedConfig(id, "stokvels");
export const saveStokvelBankFeedLink = (id, link) => saveBankFeedLink(id, link, "stokvels");
export const syncStokvelBankFeed = (id) => syncBankFeed(id, "stokvels");
